import { Store } from '../types';

export const STORE_DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export const STORE_OPERATION_DAY_OPTIONS = [
  { value: 1, label: '월' },
  { value: 2, label: '화' },
  { value: 3, label: '수' },
  { value: 4, label: '목' },
  { value: 5, label: '금' },
  { value: 6, label: '토' },
  { value: 0, label: '일' },
];

export const STORE_HOUR_OPTIONS = Array.from({ length: 25 }, (_, hour) => ({
  value: hour,
  label: `${hour.toString().padStart(2, '0')}:00`,
}));

const DEFAULT_UNMANNED_LABEL = '무인 수령';

export const toggleOperationDay = (days: number[], day: number) => {
  const next = days.includes(day)
    ? days.filter((value) => value !== day)
    : [...days, day];

  return STORE_OPERATION_DAY_OPTIONS.map((option) => option.value).filter(
    (value) => next.includes(value),
  );
};

export const formatOperationDays = (days?: number[] | null) => {
  if (!days || days.length === 0) {
    return '';
  }

  const daySet = new Set(days);
  return STORE_OPERATION_DAY_OPTIONS.filter((option) => daySet.has(option.value))
    .map((option) => `${option.label}요일`)
    .join(', ');
};

export const formatOperationHours = (
  startHour?: number | null,
  endHour?: number | null,
) => {
  if (
    startHour === undefined ||
    startHour === null ||
    endHour === undefined ||
    endHour === null
  ) {
    return '';
  }

  return `${startHour.toString().padStart(2, '0')}:00 - ${endHour
    .toString()
    .padStart(2, '0')}:00`;
};

export const getStorePickupUnmannedLabel = (store?: Partial<Store> | null) =>
  store?.pickupUnmannedLabel?.trim() || DEFAULT_UNMANNED_LABEL;

export const getPickupHourExtraLabel = (
  hour: number,
  pickupUnmannedStartHour?: number | null,
  pickupUnmannedLabel?: string | null,
) => {
  if (
    pickupUnmannedStartHour === undefined ||
    pickupUnmannedStartHour === null ||
    hour < pickupUnmannedStartHour
  ) {
    return '';
  }

  return pickupUnmannedLabel?.trim() || DEFAULT_UNMANNED_LABEL;
};

export const getStorePickupHourExtraLabel = (
  store: Partial<Store> | null | undefined,
  hour: number,
) =>
  getPickupHourExtraLabel(
    hour,
    store?.pickupUnmannedStartHour,
    store?.pickupUnmannedLabel,
  );

export const shouldCollectStorePickupTime = (store?: Partial<Store> | null) =>
  store?.orderType === 'STORE' && store?.acceptsPickupTime !== false;

export const shouldCollectStoreRequestMessage = (
  store?: Partial<Store> | null,
) => store?.orderType === 'STORE' && store?.acceptsRequestMessage !== false;

export const formatStoreOperationSummary = (store: Partial<Store>) => {
  if (store.orderType === 'STORE') {
    const daysText = formatOperationDays(store.operationDayOfWeeks);
    const hoursText = formatOperationHours(
      store.operationStartHour,
      store.operationEndHour,
    );
    const unmannedText =
      store.pickupUnmannedStartHour !== undefined &&
      store.pickupUnmannedStartHour !== null
        ? `${store.pickupUnmannedStartHour
            .toString()
            .padStart(2, '0')}:00 이후 ${getStorePickupUnmannedLabel(store)}`
        : '';
    const arrivalDateText = store.supportsArrivalDateFilter
      ? '상품별 입고날짜 필터'
      : '';
    const parts = [daysText, hoursText, unmannedText, arrivalDateText].filter(Boolean);
    return parts.length > 0 ? parts.join(' / ') : '운영 정보 미설정';
  }

  const deliveryDay = store.defaultDeliveryDay ?? store.deliveryDayOfWeek;
  if (deliveryDay === undefined) {
    return '배송 요일 미설정';
  }

  return `${STORE_DAY_LABELS[deliveryDay]}요일 배송`;
};

const toLocalDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const buildPickupDateOptions = (
  operationDayOfWeeks?: number[] | null,
  startHour?: number | null,
  endHour?: number | null,
  horizonDays = 21,
  now = new Date(),
) => {
  if (
    !operationDayOfWeeks ||
    operationDayOfWeeks.length === 0 ||
    startHour === undefined ||
    startHour === null ||
    endHour === undefined ||
    endHour === null
  ) {
    return [];
  }

  const normalizedNow = new Date(now);
  const minHourForToday =
    normalizedNow.getMinutes() > 0
      ? normalizedNow.getHours() + 1
      : normalizedNow.getHours();

  return Array.from({ length: horizonDays }, (_, index) => {
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + index);
    return date;
  })
    .filter((date) => {
      if (!operationDayOfWeeks.includes(date.getDay())) {
        return false;
      }

      const isToday = toLocalDateValue(date) === toLocalDateValue(normalizedNow);
      if (!isToday) {
        return true;
      }

      return minHourForToday < endHour;
    })
    .map((date) => ({
      value: toLocalDateValue(date),
      label: date.toLocaleDateString('ko-KR', {
        month: 'numeric',
        day: 'numeric',
        weekday: 'short',
      }),
    }));
};

export const buildPickupHourOptions = (
  startHour?: number | null,
  endHour?: number | null,
  selectedDateValue?: string,
  now = new Date(),
  pickupUnmannedStartHour?: number | null,
  pickupUnmannedLabel?: string | null,
) => {
  if (
    startHour === undefined ||
    startHour === null ||
    endHour === undefined ||
    endHour === null
  ) {
    return [];
  }

  const isToday = selectedDateValue === toLocalDateValue(now);
  const minHour = isToday
    ? Math.max(
        startHour,
        now.getMinutes() > 0 ? now.getHours() + 1 : now.getHours(),
      )
    : startHour;

  return Array.from({ length: Math.max(0, endHour - minHour) }, (_, index) => {
    const hour = minHour + index;
    const extraLabel = getPickupHourExtraLabel(
      hour,
      pickupUnmannedStartHour,
      pickupUnmannedLabel,
    );

    return {
      value: hour,
      label: extraLabel
        ? `${hour.toString().padStart(2, '0')}:00 (${extraLabel})`
        : `${hour.toString().padStart(2, '0')}:00`,
    };
  });
};
